import { ORDER_BY_TRAIT_ID, TraitId } from '@cibo/core'
import { ascend, sort, uniq } from 'ramda'
import { useProgramConfig, useWorkflow } from '../queries'

export const useWorkflowCompletion = (workflowId: string, programId: string) => {
  const {
    data: workflow,
    isLoading: loadingWorkflow,
    error: workflowError,
  } = useWorkflow(workflowId)
  const {
    data: programModel,
    isLoading: loadingProgram,
    error: programError,
  } = useProgramConfig({ programId })

  if (!programId || !workflow) {
    return null
  }

  const traits = programModel
    ?.requiredTraits(workflow.benchmark)
    .filter(traitId => traitId !== 'geometry' && traitId !== 'name')

  const statuses = traits?.map(traitId => {
    const missing =
      workflow?.fieldCounts.detailCounts[`${traitId}.missingData`] &&
      workflow?.fieldCounts.detailCounts[`${traitId}.missingData`].count > 0
    const failing =
      Object.keys(workflow?.fieldCounts.detailCounts)
        .filter(k => k.startsWith(traitId) && !k.endsWith('.missingData'))
        .map(k => workflow?.fieldCounts.detailCounts[k].count)
        .reduce((a, c) => a + c, 0) > 0

    return {
      traitId,
      failing,
      missing,
      missingOptional: !!workflow?.fieldCounts?.optionalDetailsWithMissingData?.find(
        a => a === traitId
      ),
    }
  })

  const benchmarkOrder: Partial<Record<TraitId, number>> = {}
  const benchmark = programModel?.benchmarks?.find(
    ({ benchmark }) => benchmark === workflow.benchmark
  )

  !!benchmark?.details &&
    uniq(benchmark.details.map(({ traitId }) => traitId)).forEach(
      (traitId, index) => (benchmarkOrder[traitId] = index)
    )

  const sortedTraitStatuses = sort(
    ascend(({ traitId }) => benchmarkOrder[traitId] ?? ORDER_BY_TRAIT_ID[traitId] ?? 0),
    statuses || []
  )

  return {
    isLoading: loadingProgram || loadingWorkflow,
    error: programError || workflowError,
    statuses: sortedTraitStatuses,
  }
}
